import { AppNS } from 'app';
import { Params } from 'app/types';

import { apiGetAgencyStaff } from '../invitations';

interface DisciplineData {
  disciplinesIds: AppNS.DisciplineId[];
  disciplinesData: StaffDisciplineData[];
}

export interface StaffDisciplineData {
  id: AppNS.DisciplineId;
  staff: {
    inspectors: StaffItemDisciplineData[];
    pic: StaffItemDisciplineData[];
  };
}

export interface StaffItemDisciplineData {
  id: string;
  name: string;
  roleType: AppNS.InspectorRoleType;
  invitationState: AppNS.InvitationState;
  inspectorProfileId: string;
  inspectorDisciplineState: AppNS.InspectorDisciplineState;
}

const ALLOWED_INVITATION_STATES: AppNS.InvitationState[] = ['pending', 'accepted'];
export const ALLOWED_DISCIPLINE_STATES: AppNS.InspectorDisciplineState[] = ['pending', 'approved' /*, 'draft'*/]; // draft is for tests only
export const PIC_INSPECTOR_ROLES: AppNS.InspectorRoleType[] = [
  'picInspector',
  'picPlanReviewer',
  'picWelder',
  'picCommissioner',
  'picTester',
  'picPrincipalInCharge',
  //'specialInspector',
];

export async function getDisciplines(companyId: string, signal?: AbortSignal): Promise<DisciplineData | null> {
  const result = await apiGetAgencyStaff(companyId, { limit: 1000, with_disciplines: true }, signal);
  try {
    return parseResult(result.results);
  } catch (error) {
    console.error('Error fetching disciplines:', error);
    return null;
  }
}

const parseResult = (result: AppNS.Invitation[]): DisciplineData | null => {
  const filteredResults = result.filter((item) => item?.disciplines?.length);

  if (!result || !filteredResults.length) {
    return {
      disciplinesIds: [],
      disciplinesData: [],
    };
  }

  const disciplinesIds = extractAllowedUniqueDisciplinesIds(filteredResults);

  return {
    disciplinesIds,
    disciplinesData: extractDisciplinesData(filteredResults),
  };
};

const extractAllowedUniqueDisciplinesIds = (result: AppNS.Invitation[]): AppNS.DisciplineId[] => {
  const ids = result
    .filter((item) => ALLOWED_INVITATION_STATES.includes(item.state))
    .map(
      (item) =>
        item.disciplines
          ?.filter((discipline) => ALLOWED_DISCIPLINE_STATES.includes(discipline.state))
          ?.filter((discipline) => PIC_INSPECTOR_ROLES.includes(discipline.roleType))
          .map((discipline) => discipline.name) ?? []
    );

  return [...new Set(ids.flat())];
};

const extractAllUniqueDisciplinesIds = (result: AppNS.Invitation[]): AppNS.DisciplineId[] => {
  const ids = result.map((item) => {
    return (
      item.disciplines
        ?.filter((discipline) => ALLOWED_DISCIPLINE_STATES.includes(discipline.state))
        .map((discipline) => discipline.name) ?? []
    );
  });

  return [...new Set(ids.flat())];
};

const extractDisciplinesData = (result: AppNS.Invitation[]): any => {
  if (!result) {
    return null;
  }
  const disciplinesIds = extractAllUniqueDisciplinesIds(result);

  const disciplines = disciplinesIds.map((id) => {
    return {
      id, // discipline id
      staff: {
        inspectors: getDisciplineInspectors(id, result), // get all inspectors for this discipline
        pic: getDisciplinePiCs(id, result), // get all pic inspectors for this discipline
      },
    };
  });

  return disciplines;
};

const getDisciplinePiCs = (disciplineId: AppNS.DisciplineId, result: AppNS.Invitation[]): Params[] => {
  return getDisciplineStaff(disciplineId, result, (roleType: AppNS.InspectorRoleType) => {
    return PIC_INSPECTOR_ROLES.includes(roleType);
  });
};

const getDisciplineInspectors = (disciplineId: AppNS.DisciplineId, result: AppNS.Invitation[]): Params[] => {
  return getDisciplineStaff(disciplineId, result, (roleType: AppNS.InspectorRoleType) => {
    return !PIC_INSPECTOR_ROLES.includes(roleType);
  });
};

const getDisciplineStaff = (
  disciplineId: AppNS.DisciplineId,
  result: AppNS.Invitation[],
  checkRoleFn: (roleType: AppNS.InspectorRoleType) => boolean
): StaffItemDisciplineData[] => {
  return result
    .filter((item) => ALLOWED_INVITATION_STATES.includes(item.state))
    .reduce<StaffItemDisciplineData[]>((acc, item) => {
      const hasDisciplineAsInspector = item.disciplines?.find(
        (discipline) => discipline.name === disciplineId && checkRoleFn(discipline.roleType)
      );

      if (hasDisciplineAsInspector) {
        return [
          ...acc,
          {
            id: item.invitedUserId,
            name: item?.user?.fullName ?? item.userEmail,
            inspectorProfileId: item?.inspectorProfile?.id ?? '',
            roleType: hasDisciplineAsInspector.roleType,
            invitationState: item.state,
            inspectorDisciplineState: hasDisciplineAsInspector.state,
          },
        ];
      }
      return acc;
    }, []);
};
